import { useEffect, useReducer, useState } from "react";
import Filters from "./Filters";
import { Grid, Paper, TextField } from "@material-ui/core";
import styled from "styled-components";
import { initialState, reducer } from "./context";
import { RuleContext } from "./context/ruleContext";
import Actions from "./Steps/Actions";
import Target from "./Steps/Target";
import CloudStorage from "./Steps/CloudStorage";
import ApprovalSteps from "./Steps/ApprovalSteps";
import StorageFilepath from "./Steps/StorageFilepath";
import SendDelaySteps from "./Steps/TemporaryHold";
import SmimeActions from "./Steps/SmimeActions";
import SaveButton from "./SaveButton";
import useSaveRule from "../hooks/useSaveRule";
import useAsyncValidations from "../hooks/useAsyncValidations";
import { useTranslation } from "react-i18next";
import VerticalStepper from "../common/Stepper/VerticalStepper";
import TargetWarningModal from "./TargetWarningModal";
import ConfirmUpdateModal from "./ConfirmUpdateModal";
import RetentionConflictModal from "components/RetentionConflictModal";
import { RetentionConflictDetails } from "@dashboard-v3/api";
import AddAction from "./AddAction";
import ToggleStorageSteps from "./ToggleStorageSteps";
import LocaleTimezone from "./LocaleTimezone";
import { Rule } from "../common/types";

const SidePaper = styled(Paper)`
  padding: 24px;
  position: sticky;
  top: 90px;
`;

export default function RuleForm({ rule }: { rule: Rule }) {
  const [state, dispatch] = useReducer(reducer, initialState(rule));
  // El link de la security elegida dura más que los archivos (MXH-6086).
  // Cerrar el modal no borra el error: eso lo hace el próximo guardado.
  const retentionConflict =
    state.saveError?.code === "RETENTION_CONFLICT"
      ? (state.saveError.description as RetentionConflictDetails)
      : undefined;
  const [showRetentionConflict, setShowRetentionConflict] = useState(false);

  useEffect(() => {
    setShowRetentionConflict(Boolean(retentionConflict));
  }, [retentionConflict]);
  const { type, steps } = state;
  const showCloudStorageBtn = !steps.cloudStorage && !steps.storageFilepath;
  const showActionStep =
    type !== "REQUEST_APPROVAL" &&
    type !== "SEND_DELAY" &&
    type !== "SMIME_EMAILS";
  const showStorageAndCustomActions =
    type !== "BLOCK_ATTACHMENTS" &&
    type !== "REQUEST_APPROVAL" &&
    type !== "SEND_DELAY" &&
    type !== "ZIP_ATTACHMENTS" &&
    type !== "SMIME_EMAILS";

  useSaveRule(state, dispatch);
  useAsyncValidations(state, dispatch);

  return (
    <RuleContext.Provider value={{ state, dispatch }}>
      <Grid container spacing={5}>
        <Grid item xs={6} sm>
          {showActionStep && (
            <>
              <Actions />
              <WrapVerticalLine>
                <Filters type="action" />
              </WrapVerticalLine>
            </>
          )}

          {state.type === "SMIME_EMAILS" && <SmimeActions />}

          <Target />
          <WrapVerticalLine>
            <Filters type="target" />
          </WrapVerticalLine>

          {state.type === "REQUEST_APPROVAL" && (
            <>
              <ApprovalSteps />
              <WrapVerticalLine>
                <Filters type="action" />
              </WrapVerticalLine>
            </>
          )}

          {state.type === "SEND_DELAY" && (
            <>
              <SendDelaySteps />
              <WrapVerticalLine>
                <Filters type="action" />
              </WrapVerticalLine>
            </>
          )}

          {state.type === "ZIP_ATTACHMENTS" && (
            <>
              {showCloudStorageBtn && (
                <WrapVerticalLine>
                  <ToggleStorageSteps action="ADD" />
                </WrapVerticalLine>
              )}
              {!showCloudStorageBtn && (
                <>
                  <CloudStorage />
                  <VerticalLine />
                  <StorageFilepath />
                  <WrapVerticalLine>
                    <AddAction />
                  </WrapVerticalLine>
                  <>
                    <ToggleStorageSteps action="REMOVE" />
                    <VerticalLine style={{ height: "30px" }} />
                  </>
                </>
              )}
            </>
          )}

          {showStorageAndCustomActions && (
            <>
              <CloudStorage />
              <VerticalLine />
              <StorageFilepath />
              <WrapVerticalLine>
                <AddAction />
              </WrapVerticalLine>
            </>
          )}

          <SaveButton />
        </Grid>

        <Grid item xs={4}>
          <SidePaper>
            <NameInput
              value={state.rule?.name}
              onChange={ruleName =>
                dispatch({
                  type: "updateRuleLabels",
                  payload: { type: "name", input: ruleName },
                })
              }
            />

            <DescriptionInput
              value={state.rule?.description}
              onChange={description =>
                dispatch({
                  type: "updateRuleLabels",
                  payload: { type: "description", input: description },
                })
              }
            />
            <LocaleTimezone />

            <VerticalStepper
              rule={state.rule}
              currentStep={state.currentStep}
              stepsState={state.steps}
            />
          </SidePaper>
        </Grid>
      </Grid>
      <TargetWarningModal rule={rule} />
      {state.editData?.showUpdateConfirm && <ConfirmUpdateModal />}
      {retentionConflict && (
        <RetentionConflictModal
          open={showRetentionConflict}
          origin="rule"
          details={retentionConflict}
          onClose={() => setShowRetentionConflict(false)}
        />
      )}
    </RuleContext.Provider>
  );
}

function WrapVerticalLine({ children }) {
  return (
    <>
      <VerticalLine style={{ height: "30px" }} />
      {children}
      <VerticalLine style={{ height: "30px" }} />
    </>
  );
}

const VerticalLine = styled.div`
  height: 100px;
  width: 50%;
  margin: 5px 0;
  border-right: 2px dashed #8f8f8f;
`;

const DescriptionInput = ({ value, onChange }) => {
  const { t } = useTranslation("rules");
  return (
    <TextField
      variant="outlined"
      margin="normal"
      fullWidth
      multiline
      minRows={4}
      style={{ marginBottom: "24px" }}
      label={t("ruleDescription")}
      placeholder={t("typeADescripton")}
      inputProps={{ "data-testid": "rules-steps_description-textarea" }}
      InputProps={{ style: { minHeight: "120px" } }}
      value={value ?? ""}
      onChange={event => {
        onChange(event.target.value);
      }}
    />
  );
};

const NameInput = ({ value, onChange }) => {
  const { t } = useTranslation("rules");

  return (
    <TextField
      fullWidth
      label={t("ruleName")}
      margin="normal"
      placeholder={t("typeAName")}
      type="text"
      variant="outlined"
      inputProps={{ "data-testid": "rules-steps_name-input" }}
      value={value ?? ""}
      onChange={event => {
        onChange(event.target.value);
      }}
    />
  );
};
